// src/components/BlogSection.js
import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaArrowLeft, FaClock, FaTag } from 'react-icons/fa';
import { blogService } from '../../../services/blogService';

const BlogSection = ({ theme = 'dark' }) => {
  const isDark = theme === 'dark';
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 0,
    limit: 6
  });
  const sliderRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const params = {
          limit: 6,
          sortField: 'publishedAt',
          sortOrder: 'desc',
          featured: true // Only show featured blogs in the slider
        };

        const result = await blogService.getPublishedBlogs(params);
        
        if (result.status === 'success') {
          setBlogs(result.data || []);
          setPagination(result.pagination || {
            total: result.data.length,
            pages: 1,
            limit: params.limit
          });
        } else {
          setError(result.message || 'Failed to fetch blogs');
          setBlogs([]);
        }
      } catch (error) {
        console.error('Error fetching blogs:', error);
        setError(error.message || 'Failed to fetch blogs');
        setBlogs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  // Handle scroll events and arrow visibility
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const handleScroll = () => {
      const maxScroll = slider.scrollWidth - slider.clientWidth;
      const currentScroll = slider.scrollLeft;
      
      setShowLeftArrow(currentScroll > 0);
      setShowRightArrow(currentScroll < maxScroll - 10);
      
      // Update current slide based on scroll position
      const slideWidth = slider.clientWidth;
      const newSlide = Math.round(currentScroll / slideWidth);
      if (newSlide !== currentSlide) {
        setCurrentSlide(newSlide);
      }
    };

    slider.addEventListener('scroll', handleScroll);
    return () => slider.removeEventListener('scroll', handleScroll);
  }, [currentSlide]);

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying || blogs.length === 0) return;

    const interval = setInterval(() => {
      if (sliderRef.current) {
        const maxScroll = sliderRef.current.scrollWidth - sliderRef.current.clientWidth;
        const currentScroll = sliderRef.current.scrollLeft;

        if (currentScroll >= maxScroll) {
          sliderRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          sliderRef.current.scrollBy({ left: sliderRef.current.clientWidth, behavior: 'smooth' });
        }
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, blogs.length]);

  const scrollToSlide = (direction) => {
    if (!sliderRef.current) return;

    const slider = sliderRef.current;
    const slideWidth = slider.clientWidth;
    const targetScroll = direction === 'left' 
      ? slider.scrollLeft - slideWidth
      : slider.scrollLeft + slideWidth;

    slider.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });

    // Pause auto-play temporarily when manually navigating
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <section 
      id="blog" 
      className={`py-20 ${
        isDark 
          ? 'bg-gradient-to-r from-gray-900 to-black'
          : 'bg-gradient-to-r from-gray-100 to-white'
      }`}
    >
      <motion.div
        className="container mx-auto px-4"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
      >
        <motion.div 
          className="text-center max-w-4xl mx-auto mb-16"
          variants={itemVariants}
        >
          <h2 className={`text-4xl font-bold mb-6 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            Latest Insights & Articles
          </h2>
          <p className={`text-lg ${
            isDark ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Stay updated with the latest trends, insights, and innovations in technology. 
            Explore our recent articles covering AI, development, cloud computing, and more.
          </p>
        </motion.div>

        {loading ? (
          <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            Loading articles...
          </div>
        ) : error ? (
          <div className={`text-center py-12 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
            <p className="mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className={`px-4 py-2 rounded-lg ${
                isDark 
                  ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                  : 'bg-red-100 text-red-600 hover:bg-red-200'
              }`}
            >
              Try Again
            </button>
          </div>
        ) : blogs.length === 0 ? (
          <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            No articles found. Check back later for new content.
          </div>
        ) : (
          <div className="relative">
            {/* Navigation Arrows */}
            <button
              onClick={() => scrollToSlide('left')}
              className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 p-4 rounded-full transition-all duration-300 ${
                showLeftArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'
              } ${
                isDark 
                  ? 'bg-gray-800 text-white hover:bg-gray-700'
                  : 'bg-white text-gray-800 hover:bg-gray-100'
              }`}
            >
              <FaArrowLeft size={24} />
            </button>

            <button
              onClick={() => scrollToSlide('right')}
              className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 p-4 rounded-full transition-all duration-300 ${
                showRightArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'
              } ${
                isDark 
                  ? 'bg-gray-800 text-white hover:bg-gray-700'
                  : 'bg-white text-gray-800 hover:bg-gray-100'
              }`}
            >
              <FaArrowRight size={24} />
            </button>

            {/* Blog Slider */}
            <motion.div
              ref={sliderRef}
              className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory"
              variants={containerVariants}
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {blogs.map((blog, index) => (
                <motion.div
                  key={blog._id || index}
                  variants={itemVariants}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-none w-full lg:w-1/2 xl:w-1/3 p-4 snap-start"
                >
                  <div className={`h-full rounded-xl overflow-hidden transition-all duration-300 ${
                    isDark
                      ? 'bg-gray-800/50 hover:bg-gray-800/70'
                      : 'bg-white hover:bg-gray-50'
                  } backdrop-blur-sm shadow-lg`}
                  >
                    {blog.featureImage ? (
                      <div className="relative h-48 overflow-hidden">
                        <img 
                          src={blog.featureImage} 
                          alt={blog.title} 
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/400x200?text=No+Image';
                          }}
                        />
                      </div>
                    ) : (
                      <div className={`h-48 flex items-center justify-center ${
                        isDark ? 'bg-gray-700' : 'bg-gray-100'
                      }`}>
                        <span className="text-4xl">📝</span>
                      </div>
                    )}

                    <div className="p-6">
                      {/* Categories */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {blog.categoryNames?.map((category, idx) => (
                          <span 
                            key={idx}
                            className={`text-sm px-3 py-1 rounded-full ${
                              isDark 
                                ? 'bg-blue-900/30 text-blue-400'
                                : 'bg-blue-100 text-blue-600'
                            }`}
                          >
                            <FaTag className="inline-block mr-1 text-xs" />
                            {category}
                          </span>
                        ))}
                      </div>

                      {/* Title */}
                      <h3 className={`text-xl font-bold mb-3 line-clamp-2 ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>
                        {blog.title}
                      </h3>

                      {/* Description */}
                      <p className={`mb-4 line-clamp-3 ${
                        isDark ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {blog.description || blog.shortDescription}
                      </p>

                      {/* Date and Author */}
                      <div className={`flex items-center mb-4 text-sm ${
                        isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        <FaClock className="mr-2" />
                        <span>{new Date(blog.publishedAt || blog.createdAt).toLocaleDateString()}</span>
                        {blog.authorName && (
                          <>
                            <span className="mx-2">•</span>
                            <span>{blog.authorName}</span>
                          </>
                        )}
                      </div>

                      {/* CTA Button */}
                      <Link
                        to={`/blog/${blog.urlSlug}`}
                        className={`inline-flex items-center px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-300 ${
                          isDark 
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-blue-500 text-white hover:bg-blue-600'
                        } transform hover:scale-105 active:scale-95`}
                      >
                        Read Article
                        <FaArrowRight className="ml-2" />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Slide Indicators */}
            {blogs.length > 3 && (
            <div className="flex justify-center mt-8 space-x-2">
              {Array.from({ length: Math.ceil(blogs.length / 3) }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    if (sliderRef.current) {
                      sliderRef.current.scrollTo({
                        left: index * sliderRef.current.clientWidth,
                        behavior: 'smooth'
                      });
                      setCurrentSlide(index);
                    }
                  }}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    currentSlide === index 
                      ? isDark 
                        ? 'bg-blue-500 w-4' 
                        : 'bg-blue-600 w-4'
                      : isDark
                        ? 'bg-gray-600'
                        : 'bg-gray-300'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
            )}
          </div>
        )}

        {/* Bottom CTA */}
        <motion.div 
          className="text-center mt-16"
          variants={itemVariants}
        >
          <Link
            to="/blog"
            className={`inline-flex items-center px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 ${
              isDark 
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-blue-500 text-white hover:bg-blue-600'
            } transform hover:scale-105 active:scale-95`}
          >
            Explore All Articles
            <FaArrowRight className="ml-2" />
          </Link>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default BlogSection;
